import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getFtpSettings, saveFtpSettings } from 'entities/Integrations/api';
import { Form, Formik, FormikProps } from 'formik';
import { enqueueSnackbar } from 'notistack';
import { FC, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { FormSelect } from 'shared/ui/Select/FormSelect';
import { SelectOption } from 'shared/ui/Select/types';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { ActionsForDialog } from 'shared/ui/ActionsForDialog';
import { InputField } from 'shared/ui/InputField';
import { Loader } from 'shared/ui/Loader';
import { Paper } from 'shared/ui/Paper';
import { FtpSettingsValidationSchema } from 'widgets/integrations/FtpSettings/Form/consts/validation';
import { FtpSettings } from 'widgets/integrations/FtpSettings/Form/types';

export const FSForm: FC = () => {
	const { t } = useTranslation();
	const { data: ftpSettingsData } = useQuery({
		queryKey: ['ftp-settings'],
		queryFn: getFtpSettings,
	});
	const { mutate, isLoading } = useMutation({
		mutationKey: ['ftp-settings'],
		mutationFn: saveFtpSettings,
	});

	const initialValues = useMemo(
		() => ({
			host: ftpSettingsData?.data.host ?? '',
			port: ftpSettingsData?.data.port ? String(ftpSettingsData?.data.port) : '',
			path: ftpSettingsData?.data.path ?? '',
			protocol: ftpSettingsData?.data.protocol ?? '',
			username: ftpSettingsData?.data.username ?? '',
			password: '',
		}),
		[ftpSettingsData],
	);

	const handleSubmit = (values: FtpSettings) => {
		const mutationValues = { ...values, port: Number(values.port), protocol: values.protocol };
		mutate(mutationValues, {
			onSuccess: () => {
				enqueueSnackbar({
					message: t('integrations.integration_success'),
					variant: 'success',
				});
			},
			onError: (err) => {
				const defaultErrorMessage = t('integrations.integration_error');
				const errorMessage = getErrorMessage(err, defaultErrorMessage);
				enqueueSnackbar({
					message: errorMessage,
					variant: 'error',
				});
			},
		});
	};

	return (
		<Paper>
			<Formik
				initialValues={initialValues}
				enableReinitialize
				validationSchema={FtpSettingsValidationSchema}
				onSubmit={handleSubmit}
			>
				{({ values, resetForm, setFieldValue }: FormikProps<FtpSettings>) => (
					<Form>
						<InputField
							name={'host'}
							label={t('integrations.labels.host_name')}
							placeholder={t('integrations.placeholder.host_name')}
							required
						/>
						<InputField
							name={'port'}
							label={t('integrations.labels.port')}
							placeholder={t('integrations.placeholder.port')}
							required
						/>
						<InputField
							name={'path'}
							label={t('integrations.labels.path')}
							placeholder={t('integrations.placeholder.path')}
							required
						/>
						<FormSelect
							name={'protocol'}
							label={t('integrations.labels.protocol')}
							placeholder={t('integrations.placeholder.protocol')}
							options={selectOptions}
							value={values.protocol}
							onSelect={(value) => setFieldValue('protocol', value)}
							required
						/>
						<InputField
							name={'username'}
							label={t('integrations.labels.user_name')}
							placeholder={t('integrations.placeholder.user_name')}
							required
						/>
						<InputField
							name={'password'}
							label={t('integrations.labels.password')}
							placeholder={t('integrations.placeholder.password')}
							type={'password'}
							required
						/>
						<ActionsForDialog
							cancelLabel={t('shared_texts.cancel')}
							cancelAction={() => {
								resetForm();
							}}
							applyLabel={t('shared_texts.save')}
						/>
					</Form>
				)}
			</Formik>
			{isLoading && (
				<Box
					sx={{
						position: 'absolute',
						bottom: '50%',
						right: '-200px',
					}}
				>
					<Loader />
				</Box>
			)}
		</Paper>
	);
};

const selectOptions: Array<SelectOption> = [
	{
		value: 'FTP',
		label: 'FTP',
	},
	{
		value: 'FTPS',
		label: 'FTPS',
	},
	{
		value: 'SFTP',
		label: 'SFTP',
	},
];
